import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import paths from "routes/paths";
import IconButton from "components/common/iconButton";
import {
  Card,
  Breadcrumb,
  Col,
  Row,
  Spinner,
  Tab,
  Tabs,
  Modal,
  Button,
  Table,
  Form
} from "react-bootstrap";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import SubtleBadge from "components/common/subtleBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import "assets/css/site.css";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { fetchRoleAndClaims, handleLoadingAndAccess, getInitialPageSize, getProposalStatusBadge, formatCreatedBy, getTranslatedSalutation } from "../../assets/js/common";
import { useAppContext } from '../../providers/appProvider';
import ViewProposals from "components/proposals/viewProposals/viewProposals";
import moment from "moment";
import { getDomainConfig } from "../../config/domainConfig";
import { faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableFooter from "components/common/advance-table/advanceTableFooter";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import { getOccurrenceCount } from "../../utils/recurrenceHelpers";

const ViewProposalModal = ({ show, handleClose, proposalId, refreshProposalData, preloadedData }) => {
  return (
    <Modal size="lg" show={show} onHide={handleClose} backdrop="static">
      <ViewProposals
        proposalId={proposalId}
        show={show}
        handleCloseProposal={handleClose}
        refreshProposalData={refreshProposalData}
        preloadedData={preloadedData}
      />
    </Modal>
  );
};

const StaticBackdropModal = ({ show, handleClose, proposalId, deleteOffer, navigate, t }) => {
  const handleDelete = async () => {
    const success = await deleteOffer(proposalId); // Ensure proposalId is defined and passed to the function
    if (success) {
      toast.success(t("proposalDeleted"), {
        position: "top-right",
        autoClose: 5000,
      });
      navigate("/proposals"); // Navigate only if the deletion is successful
    } else {
      toast.error(t("deleteProposalFailed"), {
        position: "top-right",
        autoClose: 5000,
      });
      console.error("Failed to delete the offer and related documents");
    }
    handleClose(); // Close the modal regardless of success
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static">
      <Modal.Header>
        <Modal.Title>{t("confirmation")}</Modal.Title>
        <Button variant="close" onClick={handleClose} />
      </Modal.Header>
      <Modal.Body>{t("deleteThisProposalText")}</Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose}>
          {t("cancel")}
        </Button>
        <Button variant="primary" onClick={handleDelete}>
          {t("delete")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Main component
const ProposalDetails = () => {
  const locationData = useLocation();
  const pathSegments = locationData.pathname.split("/");
  const isWithRelationId = pathSegments.length === 5;
  const relationIdRaw = isWithRelationId ? pathSegments[2] : null;
  const proposalIdRaw = isWithRelationId ? pathSegments[3] : pathSegments[2];

  const selectedRelationId = relationIdRaw ? relationIdRaw.toUpperCase() : null;
  const proposalId = (proposalIdRaw || "").toUpperCase();
  const [data, setData] = useState([]); // State for proposal data
  const [selectedRelation, setSelectedRelation] = useState(null); // State for selected relation
  const [loading, setLoading] = useState(true); // State for loading
  const [documentData, setDocumentData] = useState([]);
  const [documentDataState, setDocumentDataState] = useState([]); // State for document data
  const navigate = useNavigate(); // Initialize useNavigate hook
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [userData, setUserData] = useState([]); // State to store user data
  const [locationId, setLocationId] = useState(null);
  const [proposalDocumentDetail, setProposalDocumentDetail] = useState(null);
  const [isEmailButtonDisabled, setIsEmailButtonDisabled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedproposalId, setSelectedproposalId] = useState(null);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const { config, setConfig } = useAppContext();
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalDetailStatus, setProposalDetailsStatus] = useState();
  const handleEditproposalDetails = (proposalId) => {
    navigate(`${paths.editProposal.replace(":proposalId", proposalId.toLowerCase())}`);
  };
  const [clientName, setClientName] = useState("");
  const [taskWithSubtasks, setTaskWithSubtasks] = useState([]); // to store tasks + subtasks
  const [isTaskLoading, setIsTaskLoading] = useState(false);
  const [selectedSubtasksMap, setSelectedSubtasksMap] = useState({});
  const [rawProposalTasks, setRawProposalTasks] = useState([]);
  const [allSubtasksByTaskId, setAllSubtasksByTaskId] = useState({});
  const [taskNameById, setTaskNameById] = useState({});
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [searchParams] = useSearchParams();
  const hasAutoOpenedModal = useRef(false);
  const openViewer = searchParams.get("openViewer");
  const [createdByUser, setCreatedByUser] = useState(null);
  const [proposalAttachments, setProposalAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [versionedDocs, setVersionedDocs] = useState([]);
  const [versionedDocsLoading, setVersionedDocsLoading] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [deletingAttachment, setDeletingAttachment] = useState(null);

  const viewProposalPreloadedData = useMemo(
    () => ({
      proposal: data,
      proposalDetails: proposalDocumentDetail,
      tasks: rawProposalTasks,
      selectedSubtasksMap,
      allSubtasksByTaskId,
      taskNameById,
    }),
    [data, proposalDocumentDetail, rawProposalTasks, selectedSubtasksMap, allSubtasksByTaskId, taskNameById]
  );

  const refreshProposalData = () => {
    setRefreshFlag(prev => !prev); // Toggle flag to trigger useEffect
  };

  const handleSendProposal = async () => {
    try {
      setIsEmailButtonDisabled(true);
      let emaildata = {
        ...proposalDocumentDetail,
        culture: localStorage.getItem("culture"),
      };
      if (
        proposalDocumentDetail.ClientEmailaddress !=
        selectedRelation[0].Emailaddress1
      ) {
        emaildata = {
          ...emaildata,
          ClientEmailaddress: selectedRelation[0].Emailaddress1,
        };
      }
      const resp = await axios.post(
        `${process.env.REACT_APP_API_URL}/emailRoute/sendProposalEmail`,
        emaildata, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      if (resp.status === 200) {
        toast.success(t("proposalSentSuccess"), {
          position: "top-right",
          autoClose: 5000,
        });
        setIsEmailButtonDisabled(false);
      }
    } catch (error) {
      setIsEmailButtonDisabled(false);
      console.error("Error sending proposal email:", error);
      toast.error(t("proposalSentError"), {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const fetchVersionedDocuments = async (proposalId) => {
    try {
      setVersionedDocsLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/getVersionedDocuments/${proposalId}`,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
      setVersionedDocs(res.data || []);
    } catch (err) {
      console.error("Error fetching versioned documents:", err);
    } finally {
      setVersionedDocsLoading(false);
    }
  };

  const handleClose = () => setShowModal(false);
  const handleShow = (proposalId) => {
    setSelectedproposalId(proposalId);
    setShowModal(true);
  };

  useEffect(() => {

    const culture = searchParams.get("culture");

    if (culture && culture !== config.language) {
      setConfig("language", culture);
    }

  }, [searchParams]);

  const handleViewProposal = async () => {
    try {
      const currentLang = localStorage.getItem("culture");
      const templateId = proposalDocumentDetail?.TemplateId;

      if (!templateId) {
        console.warn("No TemplateId found for proposal.");
        setShowProposalModal(true);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalDetailsTemplateRoute/getProposalTemplate/${proposalId}`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      const data = response?.data;
      const languageCode =
        Array.isArray(data)
          ? data[0]?.LanguageCode
          : data?.LanguageCode;

      if (languageCode && currentLang && languageCode !== currentLang) {
        toast.error(
          t("switchToTemplateLanguage", { lang: languageCode }),
          {
            position: "top-right",
            autoClose: 4000,
          }
        );
        return;
      }

      setShowProposalModal(true);
    } catch (error) {
      console.error("Error checking template language:", error);
      toast.error(t("somethingWentWrong"), {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleCloseViewProposal = () => {
    setShowProposalModal(false); // Close modal
  };

  useEffect(() => {
    const location =
      config.location.LocationId || localStorage.getItem("LocationId");
    setLocationId(location);
  }, [config.location]);

  useEffect(() => {
    if (!locationId) return;

    const fetchAllData = async () => {
      try {
        setLoading(true);

        // Step 1: Fetch the proposal
        const proposalResponse = await fetchProposal(proposalId);

        const clientIdFromProposal = proposalResponse?.Client;

        if (!clientIdFromProposal) {
          console.warn("No client ID found in proposal.");
          return;
        }

        // Step 2: Fetch the relation by RelationNumber
        const relationResponse = await fetchRelationByNumber(clientIdFromProposal);

        // Step 3: Use relation data
        if (relationResponse) {
          setClientName(relationResponse.Name);
          setSelectedRelation(relationResponse);
        }
      } catch (error) {
        console.error("Error fetching proposal or relation data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
    setDocumentDataState(documentData);
  }, [proposalId, locationId, refreshFlag]);

  const fetchProposalDocumentDetails = async (proposalDetailId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/getProposalDetailsByProposalId/${proposalDetailId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      const data = response.data;
      setProposalDocumentDetail(data);
      setProposalDetailsStatus(data?.Status);
      return data; //  return the data
    } catch (error) {
      console.error("Error fetching proposal document details:", error);
      return null;
    }
  };

  const fetchProposalAttachments = async (proposalId) => {
    try {
      setAttachmentsLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalAttachmentRoute/getByProposal/${proposalId}`,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
      setProposalAttachments(res.data || []);
    } catch (err) {
      console.error("Error fetching proposal attachments:", err);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (deletingAttachment) return;

    try {
      setDeletingAttachment(attachmentId);

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/proposalAttachmentRoute/delete/${attachmentId}`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      setProposalAttachments((prev) =>
        prev.filter((a) => a.AttachmentId !== attachmentId)
      );

      toast.success(t("documentsDeleted"), {
        position: "top-right",
      });
    } catch (err) {
      console.error("Error deleting attachment:", err);
      toast.error(t("documentDeleteError"), {
        position: "top-right",
      });
    } finally {
      setDeletingAttachment(null);
    }
  };

  const handleViewVersionedDoc = async (detail) => {
    if (viewingDocument) return;

    try {
      setViewingDocument(detail.Document);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/blobRoute/getBlob`,
        {
          params: {
            blobUrl: detail.Document,
          },
          responseType: "arraybuffer",
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      const contentType =
        response.headers["content-type"] || "application/pdf";
      const blob = new Blob([response.data], { type: contentType });
      const objectUrl = URL.createObjectURL(blob);

      window.open(objectUrl, "_blank");
    } catch (err) {
      console.error("[handleViewVersionedDoc] ERROR:", err);
      toast.error(t("attachmentViewError"), {
        position: "top-right",
      });
    } finally {
      setViewingDocument(null);
    }
  };

  const handleViewAttachment = async (attachment) => {
    if (viewingDocument) return;

    try {
      setViewingDocument(attachment.AttachmentId);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/blobRoute/getAttachment`,
        {
          params: { blobUrl: attachment.BlobUrl },
          responseType: "arraybuffer",
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );

      const contentType =
        response.headers["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);

      window.open(url, "_blank");
    } catch (err) {
      console.error("[handleViewAttachment] ERROR:", err);
      toast.error(t("attachmentViewError"), {
        position: "top-right",
      });
    } finally {
      setViewingDocument(null);
    }
  };

  const fetchProposal = async (proposalId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalRoute/getProposalById/${proposalId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      if (response.data.LocationId != locationId) {
        navigate("/proposals");
      }
      // fetchProposalDocumentDetails(response.data.ProposalDetailId);
      fetchProposalDocumentDetails(response.data.ProposalId);
      setData(response.data);
      fetchUserById(response.data.Employee);
      fetchAndShowProposalTasks(response.data.ProposalId);
      fetchProposalAttachments(response.data.ProposalId);
      fetchVersionedDocuments(response.data.ProposalId);

      return response.data; // Return fetched proposal data
    } catch (error) {
      console.error("Error fetching proposal data:", error);
    } finally {
      setLoading(false); // End loading once everything is fetched
    }
  };

  const fetchRelationByNumber = async (relationNumber) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/relationRoute/relationByNumber/${relationNumber}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching relation data:", error);
    }
  };

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "proposal",
      claimValue: "view",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  const fetchUserById = async (userNumber) => {
    try {
      if (!userNumber) return;

      // system user check
      if (userNumber.toLowerCase() === process.env.REACT_APP_SYSTEM_USER_ID?.toLowerCase()) {
        setCreatedByUser({ Name: "System" });
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/userRoute/getUserByNumber/${userNumber}`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
      setCreatedByUser(response.data);
    } catch (error) {
      console.error("Error fetching user:", error);
      setCreatedByUser(null);
    }
  };

  useEffect(() => {
    const openViewer = searchParams.get("openViewer");

    if (openViewer === "true" && data?.ProposalId) {
      setShowProposalModal(true);

      navigate(location.pathname, { replace: true });
    }
  }, [data]);

  const deleteOffer = async (proposalId) => {
    try {
      let deleteResponse;

      // Check if a document exists before trying to delete it
      if (proposalDocumentDetail && proposalDocumentDetail.ProposalDetailId) {
        // Delete the document first
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/deleteProposalDetailsById/${proposalDocumentDetail.ProposalDetailId}`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        );
      }

      // Then delete the proposal itself
      deleteResponse = await axios.delete(
        `${process.env.REACT_APP_API_URL}/proposalRoute/deleteProposalById/${proposalId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      if (deleteResponse.status === 200) {
        return true; // Success
      }
    } catch (error) {
      console.error(
        "Error deleting proposal and/or its documents:",
        error.response ? error.response.data : error.message
      );
      return false;
    }
  };

  const billingDetailsMap = {
    Cash: t("cash"),
    "Bank Transfer": t("bankTransfer"),
    "Payment Request": t("paymentRequest"),
  };

  const invoiceTypeMap = {
    Invoice: t("invoice"),
    "Proforma Invoice": "Proforma",
  };

  const frequencyMap = {
    Daily: t("daily"),
    Weekly: t("weekly"),
    Monthly: t("monthly"),
    Dagelijks: t("daily"), // Dutch to English fallback
    Wekelijks: t("weekly"),
    Maandelijks: t("monthly"),
  };

  const repetitionDurationMap = {
    "Every 2 days": t("every2Days"),
    "Every 2 weeks": t("every2Weeks"),
    "Every 2 months": t("every2Months"),
    "Elke 2 dagen": t("every2Days"),
    "Elke 2 weken": t("every2Weeks"),
    "Elke 2 maanden": t("every2Months"),
  };

  const workTypeTranslations = {
    FloorMaintenance: t("floorMaintenance"),
    WindowCleaning: t("windowCleaning"),
    RegularCleaning: t("regularCleaning"),
  };

  const loadingOrRedirect = handleLoadingAndAccess(isLoading, hasAccess);

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  const fetchAndShowProposalTasks = async (proposalId) => {
    try {
      setIsTaskLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalId}/tasks`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      const tasks = res.data || [];
      setRawProposalTasks(tasks);
      fetchSubtasksForTasks(tasks); // show tasks with subtasks
    } catch (error) {
      console.error("Error fetching proposal tasks:", error);
    } finally {
      setIsTaskLoading(false); // Hide loader
    }
  };

  const fetchSubtasksForTasks = async (tasks) => {
    const enrichedTasks = [];
    const updatedSubtaskMap = {};
    const allSubtasksAccum = {};
    const taskNameAccum = {};

    for (const task of tasks) {
      try {
        const [subtasksRes, taskDetailsRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}/proposalRoute/proposalTasks/${task.ProposalTaskId}/subtasks`, {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
          ),
          axios.get(
            `${process.env.REACT_APP_API_URL}/taskRoute/getTaskById/${task.TaskId}`, {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret
            }
          }
          ),
        ]);

        const allSubtasks = subtasksRes.data || [];
        const selectedSubtasks = allSubtasks.filter((sub) => sub.selected !== false);

        allSubtasksAccum[task.TaskId] = allSubtasks;
        taskNameAccum[task.TaskId] = taskDetailsRes.data?.TaskName || "Unnamed Task";

        updatedSubtaskMap[task.ProposalTaskId] = new Set(
          selectedSubtasks.map((s) => s.id || s.SubtaskId)
        );

        const selectedSubtaskIds = new Set(selectedSubtasks.map((s) => s.id || s.SubtaskId));
        const filteredSubtasks = allSubtasks.filter((sub) =>
          selectedSubtaskIds.has(sub.id || sub.SubtaskId)
        );

        enrichedTasks.push({
          ...task,
          label: taskDetailsRes.data?.TaskName || "Unnamed Task",
          Subtasks: filteredSubtasks.map((sub) => ({ ...sub, SubtaskId: sub.id || sub.SubtaskId, Subtask: sub.name || sub.Subtask })), //
          recurrence: {
            frequency: task.Frequency,
            repeatEvery: task.RepeatEvery,
            selectedDays: (() => {
              try {
                const raw = task.SelectedDays;
                if (Array.isArray(raw)) return raw;
                if (typeof raw === "string") {
                  const parsed = JSON.parse(raw);
                  return Array.isArray(parsed) ? parsed : [];
                }
                return [];
              } catch {
                return [];
              }
            })(),
            advancedRecurrence: (() => {
              try {
                const raw = task.SelectedDays;
                if (typeof raw === "string") {
                  const parsed = JSON.parse(raw);
                  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                    return parsed;
                  }
                }
              } catch { }
              return null;
            })(),
            monthlyDay: task.MonthlyDay,
          },
        });
      } catch (error) {
        console.error("Error enriching task:", error);
      }
    }

    setTaskWithSubtasks(enrichedTasks);
    setSelectedSubtasksMap(updatedSubtaskMap);
    setAllSubtasksByTaskId(allSubtasksAccum);
    setTaskNameById(taskNameAccum);
  };

  const RecurrenceColumns = ({ recurrence, taskLabel, rowIdx }) => {
    if (!recurrence) return null;

    const renderToggle = (dayKey, isChecked) => (
      <td
        key={dayKey}
        className="text-center recurrence-column"
      >
        {isChecked ? (
          <div className="text-success">
            <i class="fa fa-check" aria-hidden="true"></i>
          </div>
        ) : null}
      </td>
    );

    // For Daily and Weekly: Monday to Friday only
    if (recurrence.frequency === "Daily" || recurrence.frequency === "Weekly") {
      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];
      return days.map((day) =>
        renderToggle(
          day,
          recurrence.frequency === "Daily" ? true : recurrence.selectedDays?.includes(day)
        )
      );
    }

    // For Monthly
    if (recurrence.frequency === "Monthly") {
      return Array.from({ length: 12 }, (_, i) =>
        renderToggle(`month-${i}`, true)
      );
    }

    // For Yearly
    if (recurrence.frequency === "Yearly") {
      const adv = recurrence.advancedRecurrence || {};
      const activeMonths = new Set();

      if (Array.isArray(adv.dates) && adv.dates.length > 0) {
        adv.dates.forEach((entry) => {
          if (entry && entry.month !== undefined && entry.month !== null && entry.month !== "") {
            activeMonths.add(Number(entry.month));
          }
        });
      } else if (adv.yearlyType === "weekday_occurrence" && Array.isArray(adv.yearlyOccurrences)) {
        adv.yearlyOccurrences.forEach((occ) => {
          if (occ && occ.month !== undefined && occ.month !== null && occ.month !== "") {
            activeMonths.add(Number(occ.month));
          }
        });
      } else {
        const months = adv.yearlyMonth;
        if (Array.isArray(months)) {
          months.forEach((m) => activeMonths.add(Number(m)));
        } else if (months !== undefined && months !== null && months !== "") {
          activeMonths.add(Number(months));
        }
      }

      return Array.from({ length: 12 }, (_, i) =>
        renderToggle(`month-${i}`, activeMonths.has(i))
      );
    }

    return null;
  };

  const mergedDocs = useMemo(() => [
    ...proposalAttachments.map((a) => ({
      ...a,
      VersionNo: null,
      IsCurrent: false,
      Type: "attachment",
      CreatedAt: a.createdAt,
    })),
    ...versionedDocs
      .sort((a, b) => b.VersionNo - a.VersionNo)
      .map((d) => ({
        ...d,
        OriginalName: null,
        Type: "proposal",
        CreatedAt: d.CreatedAt,
      })),
  ], [proposalAttachments, versionedDocs]);

  const documentColumns = [
    {
      accessorKey: "FileName",
      header: t("fileName"),
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "py-2" },
      },
      cell: ({ row: { original } }) => {
        if (original.Type === "proposal") {
          const label = t("proposal"); // "Proposal" in EN, "Offerte" in NL
          const version = original.VersionNo != null ? ` V${original.VersionNo}` : "";
          return (
            <span className="d-flex align-items-center gap-2">
              <i className="fas fa-file me-1 text-primary" />
              <span className="fw-semi-bold">{label}{version}</span>
              {original.IsCurrent && (
                <SubtleBadge pill bg="success">
                  <p className="mb-0 data-font-size">{t("new")}</p>
                </SubtleBadge>
              )}
            </span>
          );
        }
        return (
          <span>
            <i className="fas fa-file me-2 text-primary" />
            {original.OriginalName ?? "--"}
          </span>
        );
      },
    },
    {
      accessorKey: "CreatedAt",
      header: t("createdAt"),
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "py-2" },
      },
      cell: ({ row: { original } }) =>
        original.CreatedAt
          ? moment(original.CreatedAt).format("DD-MM-YYYY HH:mm")
          : "--",
    },
    {
      accessorKey: "Action",
      header: t("action"),
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center py-2" },
      },
      cell: ({ row: { original } }) => (
        <div className="d-flex justify-content-center gap-3">
          {viewingDocument ===
            (original.Type === "proposal"
              ? original.Document
              : original.AttachmentId) ? (
            <Spinner animation="border" variant='primary' size="sm" />
          ) : (
            <FontAwesomeIcon
              title={t("view")}
              icon={faEye}
              className={`text-info ${viewingDocument ? "opacity-50" : "cursor-pointer"
                }`}
              onClick={() => {
                if (viewingDocument) return;

                original.Type === "proposal"
                  ? handleViewVersionedDoc(original)
                  : handleViewAttachment(original);
              }}
            />
          )}
          {original.Type === "attachment" &&
            (deletingAttachment === original.AttachmentId ? (
              <Spinner animation="border" variant="danger" size="sm" />
            ) : (
              <FontAwesomeIcon
                title={t("delete")}
                icon={faTrash}
                className={`text-danger ${deletingAttachment ? "opacity-50" : "cursor-pointer"
                  }`}
                onClick={() => {
                  if (deletingAttachment) return;
                  handleDeleteAttachment(original.AttachmentId);
                }}
              />
            ))}
        </div>
      ),
    },
  ];

  const attachmentTable = useAdvanceTable({
    data: mergedDocs,
    columns: documentColumns,
    selection: false,
    sortable: true,
    pagination: true,
    perPage: getInitialPageSize("attachmentTableInProposalDetails"),
  });

  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={12} xxl={12}>
          <Card className="mb-3 h-100">
            <Background image={corner4} className="bg-card" />
            <Card.Body className="position-relative pe-3 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("details")}</h5>
                  <Breadcrumb>
                    {selectedRelationId && (
                      <>
                        <Breadcrumb.Item
                          as={Link}
                          to="/relations"
                          className="fw-semi-bold mb-1 fs-9 data-font-colour"
                          active
                        >
                          {t("relation")}
                        </Breadcrumb.Item>
                        <Breadcrumb.Item
                          as={Link}
                          to={`/relations/${selectedRelationId.toLowerCase()}/details-relation`}
                          className="fw-semi-bold mb-1 fs-9 data-font-colour"
                          active
                        >
                          {selectedRelation?.Name}
                        </Breadcrumb.Item>
                      </>
                    )}
                    <Breadcrumb.Item
                      as={Link}
                      to="/proposals"
                      className="fw-semi-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("proposals")}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      className="fw-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {data.ProposalNumber}
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </Spinner>
        </div>
      ) : (
        <>
          <Row className="mb-3 g-3">
            <Col md={12}>
              <Card>
                <Card.Body>
                  <Tabs
                    defaultActiveKey="offertegegevens"
                    id="uncontrolled-tab-example "
                    transition={false}
                  >
                    <Tab
                      eventKey="offertegegevens"
                      title={t("proposalInformation")}
                      className="border-bottom border-x p-3"
                    >
                      <Row className="align-items-center">
                        <Col>
                          {/* <h5 className="mb-0">{t("proposalInformation")}</h5> */}
                        </Col>
                        <Col xs="auto" className="mt-2">
                          <div className="d-flex flex-wrap gap-2">
                            <IconButton
                              iconClassName="fs--2"
                              variant="success"
                              size="sm"
                              icon="eye"
                              onClick={handleViewProposal}
                            >
                              {t("viewProposal")}
                            </IconButton>
                            <IconButton
                              iconClassName="fs--2"
                              variant="primary"
                              size="sm"
                              icon="pencil-alt"
                              onClick={async () => { handleEditproposalDetails(proposalId); }}
                            >
                              {t("edit")}
                            </IconButton>
                            <IconButton
                              iconClassName="fs--2"
                              variant="danger"
                              size="sm"
                              icon="trash"
                              onClick={() => handleShow(proposalId)}
                            >
                              {t("delete")}
                            </IconButton>
                          </div>
                        </Col>
                      </Row>
                      <hr className="my-3"></hr>

                      <Row>
                        <Col lg xxl={6}>
                          <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour">
                            {t("proposalDetails")}
                          </h6>
                          <Row>
                            <Col xs={6} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("reference")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {data.Reference ? data.Reference : "--"}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={6} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {" "}
                                {t("createdByWithoutcolon")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {createdByUser?.Name || "--"}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={6} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {" "}
                                {t("proposalNumber")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {data.ProposalNumber}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={6} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                                {t("status")}
                              </p>
                            </Col>
                            <Col>
                              {getProposalStatusBadge(data.Status, t, false)}
                            </Col>
                          </Row>

                          <Row>
                            <Col xs={6} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("workType")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {data.TemplateName}
                              </p>
                            </Col>
                          </Row>
                        </Col>

                        <Col lg xxl={6}>
                          <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour data-font-size ">
                            {t("clientInformation")}
                          </h6>
                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("client")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {selectedRelation?.Name || "--"}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("relationNumber")}
                              </p>
                            </Col>
                            <Col>
                              <Link
                                to={paths.relationDetailsByProposalId
                                  .replace(":proposalId", proposalId.toLowerCase())
                                  .replace(":relationId", selectedRelation?.RelationId.toLowerCase())}
                                className="fw-semi-bold mb-1 data-font-colour data-font-size text-primary"
                                style={{ cursor: "pointer" }}
                              >
                                {selectedRelation?.RelationNumber || "--"}
                              </Link>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("contactPerson")}
                              </p>
                            </Col>
                            <Col>
                                <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {selectedRelation ? getTranslatedSalutation(selectedRelation, t) : "--"}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("type")}
                              </p>
                            </Col>
                            <Col>
                              {selectedRelation?.Type === "0" || selectedRelation?.Type === "1" ? (
                                <SubtleBadge
                                  pill
                                  bg={selectedRelation.Type === "0" ? "success" : "primary"}
                                  className="d-inline-flex align-items-center px-2 mb-1"
                                >
                                  <p className="mb-0 data-font-size text-wrap">
                                    {selectedRelation.Type === "0" ? t("client") : t("prospect")}
                                  </p>
                                </SubtleBadge>
                              ) : (
                                <p className="mb-0 data-font-size text-wrap">--</p>
                              )}
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("city")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {selectedRelation?.City}
                              </p>
                            </Col>
                          </Row>
                        </Col>
                      </Row>

                      <Row className="mt-4 mt-lg-3">
                        <Col lg={6} xxl={6} className="mt-4 mt-lg-2">
                          <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour data-font-size ">
                            {t("requestInformation")}
                          </h6>

                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("requestDate")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {data.Date
                                  ? moment(data.Date).format("DD-MM-YYYY")
                                  : "--"}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("expirationDate")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {data.DueDate
                                  ? moment(data.DueDate).format("DD-MM-YYYY")
                                  : "--"}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("paymentConditions")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {billingDetailsMap[data.PaymentTerms] ||
                                  data.PaymentTerms}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("invoiceType")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {invoiceTypeMap[data.InvoiceType] || data.InvoiceType}
                              </p>
                            </Col>
                          </Row>
                        </Col>

                        <Col lg={6} xxl={6} className="mt-4 mt-lg-2">
                          <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour data-font-size ">
                            {t("contractDuration")}
                          </h6>

                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("startDate")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {data.StartDate
                                  ? moment(data.StartDate).format("DD-MM-YYYY")
                                  : "--"}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("startingTime")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {data.StartTime && data.StartDate
                                  ? moment
                                    .utc(`${moment(data.StartDate).format("YYYY-MM-DD")}T${moment.utc(data.StartTime).format("HH:mm:ss")}`)
                                    .local()
                                    .format("HH:mm")
                                  : "--"}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={5} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("endDate")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {data.EndDate
                                  ? moment(data.EndDate).format("DD-MM-YYYY")
                                  : "--"}
                              </p>
                            </Col>
                          </Row>
                          {/* <Row>
                    <Col xs={5} sm={5}>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                        {t("repetitionDuration")}
                      </p>
                    </Col>
                    <Col>
                      <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                        {data.RepetitionDuration}
                      </p>
                    </Col>
                  </Row> */}
                        </Col>
                      </Row>

                      <Row className="mt-4 mt-lg-3">
                        <Col lg={6} xxl={6} className="mt-4 mt-lg-2">
                          <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour">
                            {t("commentLabel")}
                          </h6>
                          <Row>
                            <Col xs={6} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("clientComment")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {data.ClientComment ? data.ClientComment : "--"}
                              </p>
                            </Col>

                          </Row>
                          <Row>
                            <Col xs={6} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {t("proposalComment")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size ">
                                {data.ProposalComment ? data.ProposalComment : "--"}
                              </p>
                            </Col>

                          </Row>
                        </Col>

                        <Col lg={6} xxl={6} className="mt-4 mt-lg-2">
                          <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour">
                            {t("pricingSection")}
                          </h6>
                          <Row>
                            <Col xs={6} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                                {t("proposalCost")}
                              </p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                                {data.ProposalCost != null
                                  ? `${getDomainConfig().financialLabels.currencySymbol} ${parseFloat(data.ProposalCost).toLocaleString("nl-NL", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`
                                  : "--"}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={6} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size">{t("billingCycle")}</p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                                {data.BillingCycle
                                  ? { OneTime: t("oneTimeLable"), Monthly: t("monthly"), Yearly: t("yearly") }[data.BillingCycle] ?? data.BillingCycle
                                  : "--"}
                              </p>
                            </Col>
                          </Row>
                          <Row>
                            <Col xs={6} sm={5}>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size">{t("jobFrequency")}</p>
                            </Col>
                            <Col>
                              <p className="fw-semi-bold mb-1 data-font-colour data-font-size">
                                {data.JobFrequency && data.JobFrequencyCount
                                  ? `${data.JobFrequencyCount}× ${{ PerDay: t("perDay"), PerWeek: t("perWeek"), PerMonth: t("perMonthLable"), PerYear: t("perYear") }[data.JobFrequency] ?? data.JobFrequency
                                  }`
                                  : data.JobFrequency
                                    ? ({ PerDay: t("perDay"), PerWeek: t("perWeek"), PerMonth: t("perMonthLable"), PerYear: t("perYear") }[data.JobFrequency] ?? data.JobFrequency)
                                    : "--"}
                              </p>
                            </Col>
                          </Row>
                        </Col>
                      </Row>

                    </Tab>
                    <Tab
                      eventKey="Werkprogrammma"
                      title={t("workPlan")}
                      className="border-bottom border-x p-3"
                    >
                      <>
                        {isTaskLoading ? (
                          <div className="text-center my-5">
                            <Spinner animation="border" role="status">
                              <span className="visually-hidden">{t("loadingTasks")}</span>
                            </Spinner>
                          </div>
                        ) : (
                          <Row className="g-3 mb-3">
                            <Col lg={12} xxl={12}>
                              <Card className="mb-3 h-100 no-shadow">
                                {/* <Card.Body className="position-relative pe-4 pb-2"> */}
                                <Row className="flex-between-center">
                                  <Col md>
                                    <h5 className="mb-2 mb-md-0">{t("tasks")}</h5>
                                  </Col>
                                </Row>
                                <Row>
                                  {taskWithSubtasks.length === 0 ? (
                                    <div className="text-muted text-center mt-3">
                                      {t("noTasksAvailable")}
                                    </div>
                                  ) : (
                                    taskWithSubtasks.map((task, i) => (
                                      <Card key={task.ProposalTaskId} className="mb-3 no-shadow">
                                        <Card.Header>
                                          <div className="d-flex align-items-center gap-2">
                                            <h5 className="fw-bold mb-0">
                                              {i + 1}. {task.label}{" "}
                                              {task.recurrence.frequency.toLowerCase() === "onetime" ? (
                                                <>({t("frequencyLabel.onetime")})</>
                                              ) : (
                                                <>
                                                  ({getOccurrenceCount(task.recurrence)}x{" "}
                                                  {t(
                                                    `frequencyLabel.${task.recurrence.frequency.toLowerCase()}`
                                                  )}
                                                  )
                                                </>
                                              )}
                                            </h5>
                                          </div>
                                        </Card.Header>

                                        <Card.Body className="p-0">
                                          <Table id="taskTable" bordered hover responsive size="sm">
                                            <thead>
                                              <tr>
                                                <th className="bg-primary-subtle task-column-size">
                                                  {t("subTasks")}
                                                </th>

                                                {task.recurrence.frequency.toLowerCase() === "daily" ||
                                                  task.recurrence.frequency.toLowerCase() === "weekly" ? (
                                                  [
                                                    "monday",
                                                    "tuesday",
                                                    "wednesday",
                                                    "thursday",
                                                    "friday",
                                                    "saturday",
                                                    "sunday",
                                                  ].map((day, idx) => (
                                                    <th
                                                      key={idx}
                                                      className="text-center bg-primary-subtle recurrence-column"
                                                    >
                                                      {t(`days.${day}`)}
                                                    </th>
                                                  ))
                                                ) : task.recurrence.frequency.toLowerCase() === "onetime" ? null : (
                                                  moment.months().map((month, idx) => (
                                                    <th
                                                      key={idx}
                                                      className="text-center bg-primary-subtle recurrence-column"
                                                    >
                                                      {t(`shortMonths.${month.toLowerCase()}`)}
                                                    </th>
                                                  ))
                                                )}
                                              </tr>
                                            </thead>

                                            <tbody>
                                              {task.Subtasks.map((sub, idx) => (
                                                <tr key={idx} className="m-0">
                                                  <td className="task-column-size">
                                                    <Form.Check
                                                      type="checkbox"
                                                      id={`subtask-${task.ProposalTaskId}-${sub.SubtaskId}`}
                                                      label={sub.Subtask}
                                                      defaultChecked //  all subtasks checked by default
                                                      onChange={(e) =>
                                                        handleSubtaskCheckChange(
                                                          task.ProposalTaskId,
                                                          sub.SubtaskId,
                                                          e.target.checked
                                                        )
                                                      }
                                                    />
                                                  </td>

                                                  {task.recurrence.frequency.toLowerCase() === "onetime" ? null : (
                                                    <RecurrenceColumns recurrence={task.recurrence} />
                                                  )}
                                                </tr>
                                              ))}
                                            </tbody>
                                          </Table>
                                        </Card.Body>
                                      </Card>
                                    ))
                                  )}
                                </Row>
                              </Card>
                            </Col>
                          </Row>
                        )}
                      </>
                    </Tab>
                    <Tab
                      eventKey="documents"
                      title={t("documents")}
                      className="border-bottom border-x p-3"
                    >
                      <AdvanceTableProvider {...attachmentTable}>
                        <Card className="mb-3 no-shadow">
                          <Card.Body className="p-0">
                            {attachmentsLoading || versionedDocsLoading ? (
                              <div className="text-center p-4">
                                <Spinner animation="border" role="status" />
                              </div>
                            ) : (
                              <>
                                <AdvanceTable
                                  columns={documentColumns}
                                  data={mergedDocs}
                                  headerClassName="bg-200 text-nowrap align-middle"
                                  rowClassName="align-middle white-space-nowrap"
                                  tableProps={{
                                    bordered: true,
                                    striped: true,
                                    size: "sm",
                                    className:
                                      "mb-0 overflow-hidden data-font-size table-hover",
                                  }}
                                />
                                {mergedDocs.length === 0 && (
                                  <div className="text-center pt-4">
                                    {t("noDocumentsAvailable")}
                                  </div>
                                )}
                              </>
                            )}
                          </Card.Body>
                          <Card.Footer>
                            <AdvanceTableFooter
                              rowInfo
                              navButtons
                              rowsPerPageSelection
                              tableKey="attachmentTableInProposalDetails"
                            />
                          </Card.Footer>
                        </Card>
                      </AdvanceTableProvider>
                    </Tab>
                  </Tabs>
                </Card.Body>
              </Card>
            </Col>
          </Row>

        </>
      )}
      <StaticBackdropModal
        show={showModal}
        handleClose={handleClose}
        proposalId={selectedproposalId}
        deleteOffer={deleteOffer}
        navigate={navigate}
        t={t}
      />
      <ViewProposalModal
        show={showProposalModal}
        handleClose={handleCloseViewProposal}
        proposalId={data?.ProposalId}
        refreshProposalData={refreshProposalData}
        preloadedData={viewProposalPreloadedData}
      />
    </>
  );
};

export default ProposalDetails;
